import type { ResponsePrepTime } from "@memorang/types";
import { useRef } from "react";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer, {
	type AudioPlayerRef,
} from "../../../AudioPlayerContainer";
import AudioRecorderContainer, {
	type AudioRecorderContainerRef,
	type RecordingLimitAlertProps,
} from "../../../AudioRecorderContainer";
import Box from "../../../Box";
import { isAutoRecordMode } from "../../helpers";
import useAudioResponseCompletion from "../../hooks/useAudioResponseCompletion";
import useBeep from "../../hooks/useBeep";
import usePrepSequence from "../../hooks/usePrepSequence";
import type {
	RegisterActiveResponseCompletion,
	SessionAudioPlayerTrackingHandler,
	SessionCaptureError,
	SessionRecorderTrackingHandler,
} from "../../types";
import { ItemInstruction } from "./pte/ItemInstruction";
import { PrepCountdownBanner } from "./pte/PrepCountdownBanner";
import { SubInstruction } from "./pte/SubInstruction";
import { usePrepTimerLabel } from "./pte/usePrepTimerLabel";

type Props = {
	instruction: string;
	audioUri?: string;
	subInstruction?: string;
	handleAudioPlayerTracking?: SessionAudioPlayerTrackingHandler;
	audio: string;
	handleOnRecordingComplete: (uri: string | undefined) => void;
	recordingLimitAlert?: RecordingLimitAlertProps;
	captureError: SessionCaptureError;
	handleTracking: SessionRecorderTrackingHandler;
	disabled?: boolean;
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	initialElapsedSeconds?: number;
	allowAudioReplay?: boolean;
	allowStopWhileRecording?: boolean;
	onResponseComplete?: () => void;
	onAudioEnded?: () => void;
	registerActiveResponseCompletion?: RegisterActiveResponseCompletion;
	onLimitReachedChange?: (reached: boolean) => void;
	onRecordingActiveChange?: (active: boolean) => void;
	onPlaybackDurationChange?: (durationMillis: number) => void;
};

const AnswerShortQuestion = ({
	instruction,
	audioUri,
	subInstruction,
	handleAudioPlayerTracking,
	audio,
	handleOnRecordingComplete,
	recordingLimitAlert,
	captureError,
	handleTracking,
	disabled,
	prepTime,
	timeLimit,
	initialElapsedSeconds,
	allowAudioReplay,
	allowStopWhileRecording,
	onResponseComplete,
	onAudioEnded,
	registerActiveResponseCompletion,
	onLimitReachedChange,
	onRecordingActiveChange,
	onPlaybackDurationChange,
}: Props) => {
	const { isMobile } = useDevice();
	const audioPlayerRef = useRef<AudioPlayerRef | null>(null);
	const recorderRef = useRef<AudioRecorderContainerRef | null>(null);
	const hasAudioStimulus = Boolean(audioUri);
	const isAutoRecord = isAutoRecordMode(prepTime, timeLimit);
	const playOnce = !allowAudioReplay;
	const beep = useBeep();
	const formatPrepLabel = usePrepTimerLabel();

	const { handleRecordingEnded, handleRecordingComplete } =
		useAudioResponseCompletion({
			recorderRef,
			onRecordingComplete: handleOnRecordingComplete,
			isAutoRecord,
			onRecordingEnded: beep.play,
			onResponseComplete,
			registerActiveResponseCompletion,
		});

	const { phase, remainingPrepSeconds, showBanner } = usePrepSequence({
		prepTime,
		timeLimit,
		hasAudioStimulus,
		hasRecorder: true,
		initialElapsedSeconds,
		audioPlayerRef,
		recorderRef,
		onAudioEnded,
		onRecordingStarted: beep.play,
		onRecordingEnded: handleRecordingEnded,
		enabled: isAutoRecord && !(disabled || audio),
	});

	const isPrepCountdown =
		phase === "micDelay" ||
		(phase === "initialDelay" && !showBanner && typeof prepTime !== "object");

	return (
		<Box gap={isMobile ? 16 : 24}>
			<Box gap={8}>
				<ItemInstruction instruction={instruction} />
				{subInstruction ? <SubInstruction text={subInstruction} /> : null}
				{audioUri ? (
					<>
						{showBanner ? (
							<PrepCountdownBanner
								remainingSeconds={remainingPrepSeconds}
								playOnce={playOnce}
							/>
						) : null}
						<AudioPlayerContainer
							ref={audioPlayerRef}
							uri={audioUri}
							handleTracking={handleAudioPlayerTracking}
							playOnce={playOnce}
							playOnceInteractive={Boolean(disabled)}
						/>
					</>
				) : null}
			</Box>
			<AudioRecorderContainer
				ref={recorderRef}
				recordedAudio={audio}
				handleOnRecordingComplete={handleRecordingComplete}
				recordingLimitAlert={recordingLimitAlert}
				captureError={captureError}
				handleTracking={handleTracking}
				onLimitReachedChange={onLimitReachedChange}
				onRecordingActiveChange={onRecordingActiveChange}
				onPlaybackDurationChange={onPlaybackDurationChange}
				disabled={disabled}
				type={isAutoRecord ? "autoRecord" : "default"}
				showDiscardButton={!isAutoRecord}
				allowStopWhileRecording={allowStopWhileRecording}
				maxDuration={timeLimit}
				remainingSecondsOverride={
					isPrepCountdown ? remainingPrepSeconds : undefined
				}
				formatTimerLabel={isPrepCountdown ? formatPrepLabel : undefined}
			/>
		</Box>
	);
};

export default AnswerShortQuestion;
